import { IAjaxHelper, IAjaxHelperConfig, DataDriver, AjaxResponseType } from "./types";
import { toDataDriver } from "./helpers";
import { XMLDriver } from "./drivers/XMLDriver";

function toQueryString(data: { [key: string]: any }) {
	return Object.keys(data)
		.reduce((entries, key) => {
			const value = typeof data[key] === "object" ? JSON.stringify(data[key]) : data[key];
			entries.push(key + "=" + encodeURIComponent(value));
			return entries;
		}, [])
		.join("&");
}

function inferResponseType(contentType: string): AjaxResponseType {
	if (!contentType) {
		return "text";
	}
	if (contentType.includes("json")) {
		return "json";
	}
	if (contentType.includes("xml")) {
		return "xml";
	}
	return "text";
}

function send<T>(
	url: string,
	data: any,
	method: "GET" | "POST" | "PUT" | "DELETE",
	headers?: { [key: string]: string },
	responseType?: AjaxResponseType
): Promise<T> {
	function parseResponse(responseText, genResponseType: AjaxResponseType) {
		switch (genResponseType) {
			case "json": {
				return JSON.parse(responseText);
			}
			case "text": {
				return responseText;
			}
			case "xml": {
				const driver = toDataDriver(DataDriver.xml) as XMLDriver;
				if (driver) {
					return driver.toJsonObject(responseText);
				} else {
					return { parseError: "Incorrect data driver type: 'xml'" };
				}
			}
			default: {
				return responseText;
			}
		}
	}

	const allHeaders = headers || {};

	if (responseType) {
		allHeaders.Accept = "application/" + responseType;
	}

	if (method !== "GET") {
		allHeaders["Content-Type"] = allHeaders["Content-Type"] || "application/json";
	}

	if (method === "GET") {
		const urlData =
			data && typeof data === "object"
				? toQueryString(data)
				: data && typeof data === "string"
				? data
				: "";
		if (urlData) {
			url += !url.includes("?") ? "?" : "&";
			url += urlData;
		}
		data = null;
	}

	if (!window.fetch) {
		return new Promise((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.onload = () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					if (responseType === "raw") {
						resolve({
							url: xhr.responseURL,
							headers: xhr
								.getAllResponseHeaders()
								.trim()
								.split(/[\r\n]+/)
								.reduce((acc, cur) => {
									const kv = cur.split(": ");
									acc[kv[0]] = kv[1];
									return acc;
								}, {}),
							body: xhr.response,
						} as any);
					}
					if (xhr.status === 204) {
						resolve();
					} else {
						resolve(
							parseResponse(
								xhr.responseText,
								responseType || inferResponseType(xhr.getResponseHeader("Content-Type"))
							)
						);
					}
				} else {
					reject({
						status: xhr.status,
						statusText: xhr.statusText,
					});
				}
			};
			xhr.onerror = () => {
				reject({
					status: xhr.status,
					statusText: xhr.statusText,
					message: xhr.responseText,
				});
			};
			xhr.open(method, url);
			for (const headerKey in allHeaders) {
				xhr.setRequestHeader(headerKey, allHeaders[headerKey]);
			}
			switch (method) {
				case "POST":
				case "DELETE":
				case "PUT":
					xhr.send(data !== undefined ? JSON.stringify(data) : "");
					break;
				case "GET":
					xhr.send();
					break;
				default:
					xhr.send();
					break;
			}
		});
	} else {
		return window
			.fetch(url, {
				method,
				body: data ? JSON.stringify(data) : null,
				headers: allHeaders,
			})
			.then(response => {
				if (response.ok) {
					const genResponseType =
						responseType || inferResponseType(response.headers.get("Content-Type"));
					if (genResponseType === "raw") {
						return {
							// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
							// @ts-ignore
							headers: Object.fromEntries(response.headers.entries()),
							url: response.url,
							body: response.body,
						};
					}
					if (response.status !== 204) {
						switch (genResponseType) {
							case "json": {
								return response.json();
							}
							case "xml": {
								const driver = toDataDriver(DataDriver.xml) as XMLDriver;
								if (driver) {
									return response.text().then(xmlData => driver.toJsonObject(xmlData));
								} else {
									return response.text();
								}
							}
							default:
								return response.text();
						}
					}
				} else {
					return response.text().then(message =>
						Promise.reject({
							status: response.status,
							statusText: response.statusText,
							message,
						})
					);
				}
			});
	}
}

export const ajax: IAjaxHelper = {
	get: (url: string, data?: { [key: string]: any } | string, config?: Partial<IAjaxHelperConfig>) =>
		send(
			url,
			data,
			"GET",
			config && config.headers,
			config !== undefined ? config.responseType : undefined
		),

	post: (url: string, data?: { [key: string]: any } | string, config?: Partial<IAjaxHelperConfig>) =>
		send(
			url,
			data,
			"POST",
			config && config.headers,
			config !== undefined ? config.responseType : undefined
		),

	put: (url: string, data?: { [key: string]: any } | string, config?: Partial<IAjaxHelperConfig>) =>
		send(
			url,
			data,
			"PUT",
			config && config.headers,
			config !== undefined ? config.responseType : undefined
		),

	delete: (url: string, data?: { [key: string]: any } | string, config?: Partial<IAjaxHelperConfig>) =>
		send(
			url,
			data,
			"DELETE",
			config && config.headers,
			config !== undefined ? config.responseType : undefined
		),
};
